import { BudgetBys } from 'business/constants';
import {
    DetailedDraftOperationT,
    DraftOperationT,
    OperationT,
    OperationType,
    TypedOperationT,
} from 'routes/BudgetModule/config/types';
import { RQAPICallWithData } from 'store/APIClient';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { transformUrlParams } from 'helpers/urlHelpers';
import { snakify } from 'helpers/syntaxHelper';
import { OperationToGridT } from 'routes/BudgetModule/components/BudgetOverview/AllOperations/helpers/types';
import {
    VOTED_FIELD,
    REALIZED_FIELD,
    SPLIT_BY,
} from 'routes/BudgetModule/config/constants';

export const getNonEmptyFilter =
    (
        amountFields: string[],
        exercice?: number,
    ): ((arg1: OperationToGridT) => boolean) =>
    (operation: OperationToGridT) => {
        let votedField = VOTED_FIELD as string;
        let realizedField = REALIZED_FIELD as string;
        if (exercice != null) {
            votedField = `${votedField}${SPLIT_BY}${exercice - 1}`;
            realizedField = `${realizedField}${SPLIT_BY}${exercice - 1}`;
        }

        const moneyAmountFields = [votedField, realizedField, ...amountFields];
        return moneyAmountFields.some(
            // @ts-expect-error [TS migration] Was not detected by flow
            (f) => operation[f] && operation[f] !== 0,
        );
    };

export const mergeDraftAndFinanceOperations = ({
    draftOperations,
    operations,
}: {
    draftOperations: DraftOperationT[];
    operations: OperationT[];
}) => {
    const allOperations: TypedOperationT[] = [];
    operations.forEach((operation) => {
        allOperations.push({
            ...operation,
            type: OperationType.FINANCE,
        });
    });
    draftOperations.forEach((draftOperation) => {
        allOperations.push({
            'operation.code': draftOperation.code,
            'operation.name': `${draftOperation.code} - ${draftOperation.name}`,
            'operation.softwareId': draftOperation.softwareId,
            type: OperationType.DRAFT,
        });
    });
    return allOperations;
};

const DRAFT_OPERATION_BASE_ENDPOINT = `budget/draft_operation`;

type DraftOperationAssociation = {
    budgetProposalId: number;
    resourceBy?: BudgetBys;
    resourceSoftwareId?: string;
};

const getDraftOperationsEndpoint = (
    withDetails: boolean,
    exercice?: number,
    association?: DraftOperationAssociation,
    expenseOrIncome?: string,
    budgetName?: string,
) => {
    const query = DRAFT_OPERATION_BASE_ENDPOINT;
    const queryParams = [
        { key: 'budget_name', value: budgetName },
        { key: 'budget_proposal_id', value: association?.budgetProposalId },
        { key: 'resource_by', value: association?.resourceBy },
        { key: 'resource_software_id', value: association?.resourceSoftwareId },
        { key: 'with_details', value: withDetails ? 'True' : undefined },
        { key: 'exercice', value: exercice },
        { key: 'expense_income', value: expenseOrIncome },
    ].filter((param) => !!param.value);

    const queryString = `?${queryParams
        .map((param) => `${param.key}=${param.value}`)
        .join('&')}`;

    return query + queryString;
};

export function useGetDraftOperations({
    association,
    exercice,
    expenseOrIncome,
    withDetails,
    budgetName,
}: {
    association?: DraftOperationAssociation;
    exercice?: number;
    expenseOrIncome?: string;
    withDetails: true;
    budgetName?: string;
}): UseQueryResult<DetailedDraftOperationT[]>;
export function useGetDraftOperations({
    association,
    exercice,
    expenseOrIncome,
    withDetails,
    budgetName,
}: {
    association?: DraftOperationAssociation;
    exercice?: number;
    expenseOrIncome?: string;
    withDetails?: false;
    budgetName?: string;
}): UseQueryResult<DraftOperationT[]>;
export function useGetDraftOperations({
    association,
    exercice,
    expenseOrIncome,
    withDetails = false,
    budgetName,
}: {
    association?: DraftOperationAssociation;
    exercice?: number;
    expenseOrIncome?: string;
    withDetails?: boolean;
    budgetName?: string;
}) {
    const draftOperationEndpoint = getDraftOperationsEndpoint(
        withDetails,
        exercice,
        association,
        expenseOrIncome,
        budgetName,
    );

    return useQuery({
        queryKey: [
            'draft_operations',
            budgetName || '',
            association?.budgetProposalId || '',
            association?.resourceSoftwareId || '',
            association?.resourceBy || '',
            exercice || '',
            expenseOrIncome || '',
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                (DraftOperationT | DetailedDraftOperationT)[]
            >({
                endpoint: draftOperationEndpoint,
                method: 'GET',
                preProcessingUrlCallback: (endpoint: string) =>
                    transformUrlParams(endpoint, ['resource_by'], snakify),
            });
            return response.content.data;
        },
    });
}

type AssociationPayloadT = {
    budgetProposalId: number;
    orgChartFinanceSoftwareId?: string;
    gestionnaireSoftwareId?: string;
};

export type CreateDraftOperationPayloadT = {
    name: string;
    associations: AssociationPayloadT[];
};

export const createDraftOperation = async ({
    payload,
}: {
    payload: CreateDraftOperationPayloadT;
}) => {
    const response = await RQAPICallWithData<{ message: string }>({
        endpoint: DRAFT_OPERATION_BASE_ENDPOINT,
        method: 'POST',
        body: payload,
    });
    return response.content.data;
};

export const deleteDraftOperation = async ({
    draftOperationSoftwareId,
}: {
    draftOperationSoftwareId: string;
}) => {
    const response = await RQAPICallWithData<{ message: string }>({
        endpoint: `${DRAFT_OPERATION_BASE_ENDPOINT}/${draftOperationSoftwareId}`,
        method: 'DELETE',
    });
    return response.content.data;
};

export const editDraftOperation = async ({
    payload,
    draftOperationSoftwareId,
}: {
    payload: Partial<CreateDraftOperationPayloadT>;
    draftOperationSoftwareId: string;
}) => {
    const response = await RQAPICallWithData<{ message: string }>({
        endpoint: `${DRAFT_OPERATION_BASE_ENDPOINT}/${draftOperationSoftwareId}`,
        method: 'PATCH',
        body: payload,
    });
    return response.content.data;
};
